import axios from 'axios';
import type { HourGroup } from '@/models/staffMetrics';

const API_TOKEN = '999999|GtEZBiVd7NLl1EzmCEz9DaAcswlfa6VeXu4ig7EF';

const authConfig = {
  headers: {
    Authorization: `Bearer ${API_TOKEN}`
  }
};

const BASE_URLS: Record<string, string> = {
  Berlin: 'https://schoolmaster.rackow-berlin.de',
  Frankfurt: 'https://schoolmaster.rackow-frankfurt.de',
  Liebfrauen: 'https://schoolmaster.lfsb.de',
  Kopernikus: 'https://schoolmaster.grundschule-kopernikus.de',
  Neumuehle: 'https://schoolmaster.neumuehler-schule.de',
  Paedagogium: 'https://schoolmaster.paedagogium-schwerin.de'
};

interface DashboardResponse {
  active_students: number;
  active_employees: number;
  student_target: number;
}

interface HoursAndLessonsResponse {
  hours: number;
  lessons: number;
}

interface HoursByCategoryResponse extends HoursAndLessonsResponse {
  id: number;
  label: string;
  group: HourGroup;
}

interface StaffResponse {
  average_age: number;
  amount_male: number;
  amount_female: number;
  teacher_count: number;
  staff_count: number;
  hours?: {
    pedagogical: HoursAndLessonsResponse;
    non_pedagogical: HoursAndLessonsResponse;
    unassigned?: Record<HourGroup, HoursAndLessonsResponse>;
    by_category: HoursByCategoryResponse[];
  };
}

interface SchoolFeeReductionByTypeResponse {
  reason_id: number;
  reason: string;
  count: number;
  total: number;
}

interface FinanceResponse {
  fee_sum: number;
  fee_sum_active: number;
  fee_sum_difference: number;
  paying_students: number;
  average_school_fee: number;
  last_bank_import: {
    imported_at: string | null;
    period_end: string | null;
  };
  bank_balance: number;
  last_payment_reminder_date: string;
  open_payment_reminder_sum: number;
  payment_reminders_sent_last_month: number;
  financial_certs_created: number;
  financial_certs_sent: number;
  school_fee_reductions?: {
    count: number;
    total: number;
    by_type: SchoolFeeReductionByTypeResponse[];
  };
}

interface SchoolYearTotalDifferenceResponse {
  id: number;
  name: string;
  start: string;
  end: string;
  is_current: boolean;
  total_difference: number;
}

interface TotalDifferenceResponse {
  total_difference: number | null;
  school_years?: SchoolYearTotalDifferenceResponse[];
}

export class SchoolAPIService {
  private readonly baseUrl: string;
  private readonly appId: string;

  constructor(appId: string, baseUrl: string) {
    this.baseUrl = baseUrl;
    this.appId = appId;
  }

  async getDashboardMetrics(date: string): Promise<DashboardResponse | null> {
    try {
      const response = await axios.get<DashboardResponse>(`${this.baseUrl}/api/dashboard?date=${date}`, authConfig);

      return response.data;
    } catch (error) {
      console.warn(`Error fetching active students for ${this.appId}:`, error);

      return null;
    }
  }

  async getStaffMetrics(date: string): Promise<StaffResponse | null> {
    try {
      const response = await axios.get<StaffResponse>(`${this.baseUrl}/api/dashboard/staff?date=${date}`, authConfig);

      return response.data;
    } catch (error) {
      console.warn(`Error fetching staff metrics for ${this.appId}:`, error);

      return null;
    }
  }

  async getFinanceMetrics(date: string): Promise<FinanceResponse | null> {
    try {
      const response = await axios.get<FinanceResponse>(`${this.baseUrl}/api/dashboard/finance?date=${date}`, authConfig);

      return response.data;
    } catch (error) {
      console.warn(`Error fetching finance metrics for ${this.appId}:`, error);

      return null;
    }
  }

  async getTotalDifference(date: string, forceRefresh = false): Promise<TotalDifferenceResponse> {
    const response = await axios.get<TotalDifferenceResponse>(
      `${this.baseUrl}/api/dashboard/total-difference?date=${date}&refresh=${forceRefresh}`,
      authConfig
    );

    return response.data;
  }
}

export const apiServices: Record<string, SchoolAPIService> = Object.entries(BASE_URLS).reduce(
  (acc, [appId, baseUrl]) => {
    acc[appId] = new SchoolAPIService(appId, baseUrl);
    return acc;
  },
  {} as Record<string, SchoolAPIService>
);
